import { useEffect, useRef } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import SectionHeading from '../../components/base/SectionHeading';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { attachTilt } from '../../hooks/useTiltCards';
import { ventures } from '../../mocks/portfolio';

function HowWeBuild() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.1 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 4);
  }, []);

  const items = [
    { i: 'ri-lightbulb-flash-line', t: 'Idea origination', d: 'We surface white-space opportunities from our partner network and research agenda.' },
    { i: 'ri-user-add-line', t: 'Founder pairing', d: 'We match exceptional operators with the ideas we believe must exist.' },
    { i: 'ri-flask-line', t: 'Prototype sprint', d: 'A 90-day sprint with shared engineering, design and research resources.' },
    { i: 'ri-rocket-line', t: 'Spin out', d: 'When the thesis lands, we seed the company and watch it become independent.' },
  ];
  return (
    <section ref={revealRef} className="relative bg-[#070709] py-24 md:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="Studio model"
          title="How the studio works."
          description="Atlas Labs is our in-house venture studio. Four phases, one mission — turn the best ideas into companies that ship."
          className="mb-16"
        />
        <div ref={tiltRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, idx) => (
            <div
              key={it.t}
              className="gsap-reveal tilt-card hover-glow glass-card rounded-2xl p-7 min-h-[260px] flex flex-col gap-6"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl liquid-glass tilt-lift">
                  <i className={`${it.i} text-xl`}></i>
                </div>
                <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/30">
                  0{idx + 1}
                </span>
              </div>
              <div>
                <h3 className="font-display text-xl md:text-2xl mb-2">
                  <span className="title-shimmer inline-block">{it.t}</span>
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{it.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VenturesSection() {
  const ref = useScrollReveal<HTMLDivElement>({ stagger: 0.1 });
  return (
    <section ref={ref} className="relative bg-[#050505] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="Ventures"
          title="What we’re building today."
          className="mb-16"
        />

        <div className="divide-y divide-white/5 border-y border-white/5">
          {ventures.map((v) => (
            <article
              key={v.name}
              className="gsap-reveal group grid grid-cols-12 gap-6 py-10 items-start hover:bg-white/[0.02] transition-colors px-2 md:px-4"
            >
              <div className="col-span-12 md:col-span-2 text-xs font-mono uppercase tracking-[0.25em] text-white/40">
                {v.year}
              </div>
              <div className="col-span-12 md:col-span-7">
                <div className="flex flex-wrap items-baseline gap-3 mb-3">
                  <h3 className="font-display text-3xl md:text-4xl">
                    <span className="title-shimmer inline-block">{v.name}</span>
                  </h3>
                  <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 px-2 py-1 border border-white/10 rounded-full">
                    {v.status}
                  </span>
                </div>
                <p className="text-white/65 max-w-2xl leading-relaxed">{v.description}</p>
              </div>
              <div className="col-span-12 md:col-span-3 md:text-right">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/30 mb-1">Metric</div>
                <div className="font-display text-xl">{v.metric}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CapabilitiesSection() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.08 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 3.5);
  }, []);

  const caps = [
    { t: 'Engineering', d: 'Senior full-stack, ML, and infra engineers on retainer.' },
    { t: 'Design', d: 'Brand, product and motion — a small in-house studio.' },
    { t: 'Research', d: 'Quarterly research drops on emerging technical frontiers.' },
    { t: 'Recruiting', d: 'Dedicated talent partner with a 6,000-strong network.' },
    { t: 'Legal & Finance', d: 'Operational scaffolding from day one — incorporation to runway.' },
    { t: 'Go-to-market', d: 'Hands-on GTM experiments with our portfolio operators.' },
  ];
  return (
    <section ref={revealRef} className="relative bg-[#070709] py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="Capabilities"
          title="A founding team in a box."
          description="Founders working with Atlas Labs plug into the resources every early company wishes it had on day one."
          className="mb-16"
        />
        <div ref={tiltRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {caps.map((c) => (
            <div
              key={c.t}
              className="gsap-reveal tilt-card hover-glow glass-card rounded-2xl p-7"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-2 h-2 rounded-full bg-white" />
                <h3 className="font-display text-xl">
                  <span className="title-shimmer inline-block">{c.t}</span>
                </h3>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BuildingPage() {
  return (
    <PageLayout transparentNav>
      <PageHero
        eyebrow="Building"
        title="A venture studio for the unbuilt."
        description="Atlas Labs is our in-house studio. We co-found, prototype, and incubate companies alongside exceptional operators — sharing engineering, design, and conviction."
        image="https://readdy.ai/api/search-image?query=Minimalist%20industrial%20composition%20of%20stacked%20matte%20black%20geometric%20building%20blocks%20arranged%20on%20a%20dark%20concrete%20surface%20with%20soft%20warm%20amber%20directional%20studio%20light%2C%20cinematic%20editorial%20still%20life%2C%20deep%20negative%20space%2C%20premium%20engineering%20aesthetic&width=1800&height=900&seq=vex-building-hero&orientation=landscape"
      />
      <HowWeBuild />
      <VenturesSection />
      <CapabilitiesSection />
    </PageLayout>
  );
}